import type { ItemClaim } from './AgentWorkApi.ts';
import type { ItemAgentRequests } from './AgentMessagingApi.ts';
import type { HttpClient } from '../http/HttpClient.ts';
import type { ProjectColor } from './ProjectsApi.ts';

export type ItemType = 'epic' | 'story' | 'task' | 'bug' | 'subtask';
export type StatusCategory = 'backlog' | 'todo' | 'in_progress' | 'done' | 'canceled';
export type StatusScope = 'story' | 'issue' | 'both';
export type Priority = 'low' | 'normal' | 'high' | 'urgent';
export type Severity = 'critical' | 'major' | 'minor' | 'trivial';
export type Environment = 'production' | 'staging' | 'development' | 'qa';
export type Resolution = 'fixed' | 'wont_fix' | 'duplicate' | 'cannot_reproduce' | 'by_design';
export const POINTS = [1, 2, 3, 5, 8, 13, 21] as const;

export interface Status {
  id: string;
  name: string;
  category: StatusCategory;
  appliesTo: StatusScope;
  isReview: boolean;
  color: ProjectColor;
  rank: string;
}

export interface Label {
  id: string;
  name: string;
  color: ProjectColor;
}

export interface WorkItem {
  id: string;
  key: string;
  number: number;
  type: ItemType;
  title: string;
  description: string;
  acceptanceCriteria: string;
  statusId: string;
  epicId: string | null;
  parentId: string | null;
  /** null = backlog. */
  sprintId: string | null;
  rank: string;
  priority: Priority | null;
  points: number | null;
  percentComplete: number;
  isMilestone: boolean;
  assigneeId: string | null;
  reporterId: string | null;
  startDate: string | null;
  dueDate: string | null;
  color: ProjectColor | null;
  severity: Severity | null;
  environment: Environment | null;
  resolution: Resolution | null;
  resolvedAt: string | null;
  affectedVersion: string | null;
  fixVersion: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  labelIds: string[];
  /** An agent working on it ("With Build bot (Olive's agent)"). */
  claim?: ItemClaim | null;
  /** Numbers of the open decisions it's waiting on (D-12); empty when it isn't blocked. */
  blockedBy?: number[];
}

/** List rows: items without their long text fields, plus list extras. */
export interface WorkItemSummary extends Omit<WorkItem, 'description' | 'acceptanceCriteria'> {
  descriptionPreview: string;
  subtaskCount: number;
  subtasksDone: number;
}

export interface ItemRef {
  id: string;
  number: number;
  key: string;
  type: ItemType;
  title: string;
  statusId: string;
}

export interface WorkItemDetail extends WorkItem {
  epic: ItemRef | null;
  parent: ItemRef | null;
  children: ItemRef[];
  /** Agents waiting on people about it, or asking to delete it. */
  agentRequests?: ItemAgentRequests;
  /** Decisions it's linked to (blocked while any is open). */
  decisions?: { id: string; number: number; title: string; status: 'open' | 'decided' }[];
}

export type BulkOp =
  | { op: 'update'; patch: ItemPatch }
  | { op: 'addLabels'; labelIds: string[] }
  | { op: 'replaceLabels'; labelIds: string[] }
  | { op: 'duplicate' }
  | { op: 'delete' };

export type BulkResult =
  | { id: string; ok: true; item?: WorkItemSummary }
  | { id: string; ok: false; error: { code: string; message: string } };

export type MoveToProjectResult =
  | { id: string; ok: true; key: string }
  | { id: string; ok: false; error: { code: string; message: string } };

export interface MoveRequest {
  ids: string[];
  set?: {
    epicId?: string | null;
    statusId?: string;
    sprintId?: string | null;
    assigneeId?: string | null;
  };
  afterId?: string | null;
  beforeId?: string | null;
}

export type SearchSource = 'description' | 'acceptanceCriteria' | 'subtasks';

export interface ViewPrefs {
  layout: Record<string, unknown>;
  state: Record<string, unknown>;
}

export type ItemPatch = Partial<
  Omit<
    WorkItem,
    | 'id'
    | 'key'
    | 'number'
    | 'rank'
    | 'reporterId'
    | 'resolvedAt'
    | 'startedAt'
    | 'completedAt'
    | 'createdAt'
    | 'updatedAt'
  >
>;
export type StatusInput = Pick<Status, 'name' | 'category' | 'appliesTo' | 'isReview' | 'color'>;

const base = (org: string, key: string) =>
  `/orgs/${encodeURIComponent(org)}/projects/${encodeURIComponent(key)}`;

/** Statuses, labels and work items of one project. */
export class WorkApi {
  readonly #http: HttpClient;

  constructor(http: HttpClient) {
    this.#http = http;
  }

  statuses(org: string, key: string): Promise<{ statuses: Status[] }> {
    return this.#http.get(`${base(org, key)}/statuses`);
  }

  createStatus(org: string, key: string, input: StatusInput): Promise<{ status: Status }> {
    return this.#http.post(`${base(org, key)}/statuses`, input);
  }

  updateStatus(
    org: string,
    key: string,
    id: string,
    patch: Partial<StatusInput>,
  ): Promise<{ status: Status }> {
    return this.#http.patch(`${base(org, key)}/statuses/${id}`, patch);
  }

  deleteStatus(org: string, key: string, id: string, reassignTo: string | null): Promise<void> {
    return this.#http.request('DELETE', `${base(org, key)}/statuses/${id}`, {
      body: { reassignTo },
    });
  }

  reorderStatuses(org: string, key: string, ids: string[]): Promise<{ statuses: Status[] }> {
    return this.#http.post(`${base(org, key)}/statuses/reorder`, { ids });
  }

  applyTemplate(org: string, key: string, template: string): Promise<{ statuses: Status[] }> {
    return this.#http.post(`${base(org, key)}/statuses/apply-template`, { template });
  }

  labels(org: string, key: string): Promise<{ labels: Label[] }> {
    return this.#http.get(`${base(org, key)}/labels`);
  }

  createLabel(
    org: string,
    key: string,
    input: { name: string; color: ProjectColor },
  ): Promise<{ label: Label }> {
    return this.#http.post(`${base(org, key)}/labels`, input);
  }

  updateLabel(
    org: string,
    key: string,
    id: string,
    patch: { name?: string; color?: ProjectColor },
  ): Promise<{ label: Label }> {
    return this.#http.patch(`${base(org, key)}/labels/${id}`, patch);
  }

  deleteLabel(org: string, key: string, id: string): Promise<void> {
    return this.#http.delete(`${base(org, key)}/labels/${id}`);
  }

  /**
   * A page of items in rank order (up to 1,000): pass `next` back as `after`
   * for the following one; null when there are no more.
   */
  items(
    org: string,
    key: string,
    query: {
      types?: ItemType[];
      after?: string;
      /** Only this person's items. */
      assignee?: string;
      /** Title, key or number contains this. */
      q?: string;
      limit?: number;
    },
    signal?: AbortSignal,
  ): Promise<{ items: WorkItemSummary[]; next: string | null }> {
    const { types, ...rest } = query;
    return this.#http.get(`${base(org, key)}/items`, {
      query: { ...(types?.length ? { types: types.join(',') } : {}), ...rest },
      ...(signal ? { signal } : {}),
    });
  }

  item(org: string, key: string, number: number): Promise<{ item: WorkItemDetail }> {
    return this.#http.get(`${base(org, key)}/items/${number}`);
  }

  createItem(
    org: string,
    key: string,
    input: ItemPatch & { type: ItemType; title: string },
  ): Promise<{ item: WorkItemDetail }> {
    return this.#http.post(`${base(org, key)}/items`, input);
  }

  updateItem(
    org: string,
    key: string,
    number: number,
    patch: ItemPatch,
  ): Promise<{ item: WorkItemDetail }> {
    return this.#http.patch(`${base(org, key)}/items/${number}`, patch);
  }

  deleteItem(org: string, key: string, number: number): Promise<void> {
    return this.#http.delete(`${base(org, key)}/items/${number}`);
  }

  moveItems(org: string, key: string, req: MoveRequest): Promise<{ items: WorkItemSummary[] }> {
    return this.#http.post(`${base(org, key)}/items/move`, req);
  }

  /** Move items to another project in the org; results per item, with each new key. */
  moveToProject(
    org: string,
    key: string,
    ids: string[],
    to: string,
  ): Promise<{ results: MoveToProjectResult[] }> {
    return this.#http.post(`${base(org, key)}/items/move-to-project`, { ids, to });
  }

  bulk(org: string, key: string, ids: string[], op: BulkOp): Promise<{ results: BulkResult[] }> {
    return this.#http.post(`${base(org, key)}/items/bulk`, { ids, op });
  }

  search(
    org: string,
    key: string,
    q: string,
    signal?: AbortSignal,
  ): Promise<{ matches: { id: string; sources: SearchSource[] }[]; more: boolean }> {
    return this.#http.get(`${base(org, key)}/items/search`, {
      query: { q },
      ...(signal ? { signal } : {}),
    });
  }

  /** The multi-project view's own saved setup in an organization. */
  orgViewPrefs(org: string, lens: string): Promise<{ prefs: ViewPrefs }> {
    return this.#http.get(`/orgs/${encodeURIComponent(org)}/view-prefs/${lens}`);
  }

  saveOrgViewPrefs(
    org: string,
    lens: string,
    prefs: Partial<ViewPrefs>,
  ): Promise<{ prefs: ViewPrefs }> {
    return this.#http.put(`/orgs/${encodeURIComponent(org)}/view-prefs/${lens}`, prefs);
  }

  viewPrefs(org: string, key: string, lens: string): Promise<{ prefs: ViewPrefs }> {
    return this.#http.get(`${base(org, key)}/view-prefs/${lens}`);
  }

  saveViewPrefs(
    org: string,
    key: string,
    lens: string,
    prefs: Partial<ViewPrefs>,
  ): Promise<{ prefs: ViewPrefs }> {
    return this.#http.put(`${base(org, key)}/view-prefs/${lens}`, prefs);
  }
}
